<template>
  <div class="rp-page">
    <div class="rp-glow rp-glow--1" />
    <div class="rp-glow rp-glow--2" />

    <div class="rp-card">
      <div class="rp-brand">
        <img src="@/assets/logo.png" class="rp-brand__logo" />
      </div>

      <h1 class="rp-title">Restablecer contraseña</h1>
      <p class="rp-subtitle">Elige una contraseña segura para tu cuenta.</p>

      <form @submit.prevent="recoverPassword" class="rp-form">

        <div class="rp-field">
          <label class="rp-label">Nueva contraseña</label>
          <div class="control has-icons-right">
            <input
              class="input"
              :type="!showPassword ? 'password' : 'text'"
              required
              name="password"
              placeholder="••••••••••"
              autofocus=""
              autocomplete="off"
              v-model="password"
            />
            <a class="icon is-small is-right rp-eye" @click="viewPassword()">
              <i class="far fa-eye" v-if="!showPassword" />
              <i class="far fa-eye-slash" v-else />
            </a>
          </div>
          <div v-if="validPassword" class="rp-inline-msg" :class="isValid ? 'is-valid' : 'is-invalid'">
            <i :class="isValid ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'" />
            {{ validPassword }}
          </div>
        </div>

        <div class="rp-field">
          <label class="rp-label">Confirmar contraseña</label>
          <div class="control has-icons-right">
            <input
              class="input"
              :type="!showRepeatedPassword ? 'password' : 'text'"
              name="password2"
              placeholder="••••••••••"
              autocomplete="off"
              v-model="repeatedPassword"
            />
            <a class="icon is-small is-right rp-eye" @click="viewRepeatedPassword()">
              <i class="far fa-eye" v-if="!showRepeatedPassword" />
              <i class="far fa-eye-slash" v-else />
            </a>
          </div>
          <div v-if="passwordMatch" class="rp-match-badge" :class="{ 'is-match': isMatch }">
            <i :class="isMatch ? 'fas fa-check-circle' : 'fas fa-times-circle'" />
            {{ passwordMatch }}
          </div>
        </div>

        <div class="rp-error" v-if="error">{{ error }}</div>

        <button
          class="rp-btn"
          :class="{ 'is-loading': loading }"
          :disabled="!isDisabled"
        >
          Restablecer contraseña
        </button>
      </form>

      <div class="rp-divider" />

      <router-link class="rp-back" :to="{ name: 'login' }">
        <i class="fas fa-arrow-left" /> Volver al inicio de sesión
      </router-link>
    </div>

    <p class="rp-tagline">Smart Native</p>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'

export default {
  name: 'ResetPassword',
  props: ['token'],
  data () {
    return {
      recoveryToken: this.token,
      password: null,
      repeatedPassword: null,
      isValid: false,
      isMatch: false,
      button: false,
      regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{6,})/,
      showPassword: false,
      showRepeatedPassword: false,
      loading: false,
      error: null
    }
  },
  computed: {
    ...mapState({ authentication: state => state.authentication }),
    passwordMatch () {
      if (this.password && this.repeatedPassword) {
        if (this.password !== this.repeatedPassword) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.isMatch = false
          return 'Las contraseñas no coinciden'
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.isMatch = true
          return 'Las contraseñas coinciden'
        }
      }
      return null
    },
    validPassword () {
      if (this.password) {
        if (this.regex.test(this.password)) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.isValid = true
          return 'La contraseña es válida'
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.isValid = false
          return 'La contraseña debe tener un número (0-9), al menos una letra minúscula (a-z) y mayúscula (A-Z), un carácter especial (@#$%&!) y 6 caracteres de largo.'
        }
      }
      return null
    },
    // eslint-disable-next-line vue/return-in-computed-property
    isDisabled () {
      if (this.isValid && this.isMatch) {
        return !this.button
      }
    }
  },
  methods: {
    ...mapMutations(['setAuthenticationValue']),
    ...mapActions(['resetPass']),
    recoverPassword () {
      this.loading = true
      this.error = null
      this.resetPass({ token: this.recoveryToken, password: this.password })
        .then(resp => {
          this.loading = false
          if (resp) this.$router.push('/login')
        })
        .catch(() => {
          this.loading = false
          this.error = 'El enlace de recuperación no es válido o ha expirado.'
        })
    },
    viewPassword () { this.showPassword = !this.showPassword },
    viewRepeatedPassword () { this.showRepeatedPassword = !this.showRepeatedPassword }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.rp-page {
  position: relative;
  min-height: 100vh;
  background: #0d1525;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 2rem;
}

.rp-glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;

  &--1 {
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(76, 112, 255, 0.22), transparent 65%);
    filter: blur(60px);
    top: -200px;
    right: -150px;
  }
  &--2 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(76, 112, 255, 0.1), transparent 70%);
    filter: blur(80px);
    bottom: -100px;
    left: -80px;
  }
}

.rp-card {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2.5rem 2.25rem;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.5);
}

.rp-brand {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;

  &__logo {
    width: 200px;
    opacity: 0.92;
  }
}

.rp-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.3rem;
  text-align: center;
}

.rp-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 2rem;
  text-align: center;
}

.rp-form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.rp-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  .input {
    height: 35px;
  }
}

.rp-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.rp-eye {
  pointer-events: all !important;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
}

.rp-inline-msg {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  font-size: 0.78rem;
  padding-left: 0.5rem;
  border-left: 3px solid;
  line-height: 1.5;

  i { font-size: 0.8rem; margin-top: 0.15rem; flex-shrink: 0; }

  &.is-valid {
    color: #2ecc87;
    border-color: #2ecc87;
  }
  &.is-invalid {
    color: #ff3860;
    border-color: #ff3860;
  }
}

.rp-match-badge {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.65rem;
  border-radius: 20px;
  background-color: rgba(255, 56, 96, 0.12);
  color: #ff3860;

  i { font-size: 0.75rem; }

  &.is-match {
    background-color: rgba(46, 204, 135, 0.12);
    color: #2ecc87;
  }
}

.rp-error {
  font-size: 0.82rem;
  color: #ff3860;
  background: rgba(255, 56, 96, 0.1);
  border: 1px solid rgba(255, 56, 96, 0.25);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
}

.rp-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 46px;
  background: $blue;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    background: darken(#4c70ff, 8%);
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &.is-loading {
    color: transparent;
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.4);
      border-top-color: #fff;
      border-radius: 50%;
      animation: rp-spin 0.6s linear infinite;
    }
  }
}

@keyframes rp-spin {
  to { transform: rotate(360deg); }
}

.rp-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 1.5rem 0 1.25rem;
}

.rp-back {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: #7b9fff;
  text-decoration: none;

  &:hover { color: #fff; text-decoration: underline; }
}

.rp-tagline {
  margin-top: 1.25rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.25);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
</style>
